// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
// Licensed under the Apache License, Version 2.0 (https://www.apache.org/licenses/LICENSE-2.0).

// Global flash messages (requirement 9.2) and the shared error display: the message plus the request's reference
// (the API's trace ID, or else the HTTP API request ID) with a copy button for support (docs/DEPLOY.md §7a).
import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import Box from '@cloudscape-design/components/box';
import CopyToClipboard from '@cloudscape-design/components/copy-to-clipboard';
import type { FlashbarProps } from '@cloudscape-design/components/flashbar';

type Item = FlashbarProps.MessageDefinition;
interface Notify {
  items: Item[];
  success: (content: string) => void;
  /**
   * With `error` (the caught value), an API failure also shows its reference. `action` is a short label of what the
   * user did ("Suggest fields"), put on the copied reference line; without it the line has the method and route.
   */
  error: (content: string, error?: unknown, action?: string) => void;
  info: (content: string) => void;
}

const Ctx = createContext<Notify | null>(null);
let seq = 0;

export function NotifyProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<Item[]>([]);
  const push = useCallback((type: Item['type'], content: ReactNode) => {
    const id = `n${++seq}`;
    const dismiss = () => setItems((xs) => xs.filter((x) => x.id !== id));
    setItems((xs) => [
      ...xs,
      { id, type, content, dismissible: true, onDismiss: dismiss, dismissLabel: 'Dismiss message' },
    ]);
    if (type === 'success' || type === 'info') window.setTimeout(dismiss, 6000);
  }, []);
  return (
    <Ctx.Provider
      value={{
        items,
        success: (c) => push('success', c),
        error: (c, e, action) => {
          const reference = referenceOf(e);
          push('error', reference ? <MessageWithReference message={c} reference={reference} action={action} /> : c);
        },
        info: (c) => push('info', c),
      }}
    >
      {children}
    </Ctx.Provider>
  );
}

export function useNotify(): Notify {
  const c = useContext(Ctx);
  if (!c) throw new Error('useNotify outside NotifyProvider');
  return c;
}

export const errorMessage = (e: unknown): string => (e instanceof Error ? e.message : 'Something went wrong.');

/** What identifies a request for support: its trace ID and/or HTTP API request ID, when, and where it went. */
export interface Reference {
  /** X-Ray root trace ID (`x-trace-id`); finds the trace and every log line of the request. */
  traceId?: string;
  /** HTTP API request ID (`apigw-requestid`); finds the access log line, and the Api log lines (`requestId`). */
  requestId?: string;
  at?: Date | string;
  /** Method and id-free route (`ApiError.route`), the copied line's action when the caller gives no label. */
  method?: string;
  route?: string;
}

const str = (v: unknown): string | undefined => (typeof v === 'string' && v ? v : undefined);

/** The X-Ray trace ID of a failed API request (`ApiError.traceId`), when the API returned one. */
export function traceIdOf(e: unknown): string | undefined {
  return str((e as { traceId?: unknown } | null)?.traceId);
}

/** The reference of a failed API request (ApiError), or undefined when it has neither a trace ID nor a request ID. */
export function referenceOf(e: unknown): Reference | undefined {
  const x = (e ?? {}) as Record<string, unknown>;
  const traceId = str(x.traceId);
  const requestId = str(x.requestId);
  if (!traceId && !requestId) return undefined;
  return {
    traceId,
    requestId,
    at: x.at instanceof Date ? x.at : undefined,
    method: str(x.method),
    route: str(x.route),
  };
}

/** `2026-11-05 00:39:13 UTC`, or undefined for a missing or invalid time. */
export function utcTime(at: Date | string | undefined): string | undefined {
  const d = at === undefined ? undefined : new Date(at);
  if (!d || Number.isNaN(d.getTime())) return undefined;
  return `${d.toISOString().slice(0, 19).replace('T', ' ')} UTC`;
}

/**
 * The single line a user copies for support: `Reference <trace ID> · <UTC time> · <action>`, or `Request <request
 * ID> · ...` without a trace ID. The action is the caller's label, else the method and id-free route.
 */
export function referenceLine(reference: Reference, action?: string): string {
  const id = reference.traceId ? `Reference ${reference.traceId}` : `Request ${reference.requestId ?? ''}`;
  const what = action || [reference.method, reference.route].filter(Boolean).join(' ') || undefined;
  return [id, utcTime(reference.at), what].filter(Boolean).join(' · ');
}

/**
 * "Reference ID: <trace ID>" (or "Request ID: <request ID>" without one) with a copy button that copies the
 * reference line (referenceLine). Nothing without either id.
 */
export function ReferenceBlock({
  reference,
  action,
  label,
}: {
  reference?: Reference | null;
  action?: string;
  /** Replaces "Reference ID" (for example "Completion reference ID" on the run page). */
  label?: string;
}) {
  if (!reference || (!reference.traceId && !reference.requestId)) return null;
  const shownLabel = reference.traceId ? (label ?? 'Reference ID') : 'Request ID';
  const shownId = reference.traceId ?? reference.requestId!;
  return (
    <Box variant="small" color="text-body-secondary" display="block" margin={{ top: 'xxs' }}>
      {shownLabel}:{' '}
      <CopyToClipboard
        variant="inline"
        textToCopy={referenceLine(reference, action)}
        textToDisplay={shownId}
        copyButtonAriaLabel={`Copy ${shownLabel}`}
        copySuccessText="Reference copied. Send it to support with a description of the problem."
        copyErrorText="Reference could not be copied"
      />
    </Box>
  );
}

export function MessageWithReference({
  message,
  reference,
  action,
}: {
  message: ReactNode;
  reference?: Reference | null;
  action?: string;
}) {
  return (
    <>
      {message}
      <ReferenceBlock reference={reference} action={action} />
    </>
  );
}

/** The shared error text for a caught value: its message and, for an API failure, its reference. */
export function ErrorDetail({ error, action }: { error: unknown; action?: string }) {
  return <MessageWithReference message={errorMessage(error)} reference={referenceOf(error)} action={action} />;
}
